/** Page actions shared by the right-click menu and the page tree's shortcuts. */
import { useTranslation } from 'react-i18next';
import { copyPageToWorkspace, duplicatePage } from '@nexus/workspace';
import { useStore } from '../db/context';
import { useUi } from '../state/ui';
import { refreshWorkspacesSoon } from '../workspaces/state';

export const PAGE_KEYS = {
  duplicate: 'mod+d',
  rename: 'mod+shift+r',
  move: 'mod+shift+p',
  newTab: 'mod+shift+enter',
};

export function usePageActions() {
  const { t } = useTranslation();
  const store = useStore();
  const { open, set } = useUi();
  return {
    duplicate: async (pageId: string) => {
      const copy = await duplicatePage(store, pageId, (title) =>
        t('pageMenu.copyTitle', { title: title || t('page.untitled') }),
      );
      if (copy) open({ kind: 'page', pageId: copy.id });
    },
    move: (pageId: string) => set({ moveTarget: pageId }),
    newTab: (pageId: string) => open({ kind: 'page', pageId }, true),
    trash: (pageId: string) => void store.trashPage(pageId),
    /** Share an existing private page -- and its subtree -- into a workspace, in place. */
    shareToWorkspace: async (pageId: string, workspaceId: string) => {
      await store.movePageToWorkspace(pageId, workspaceId);
      refreshWorkspacesSoon();
    },
    /** A copy of a private page in a workspace; the original stays where it is. */
    copyToWorkspace: async (pageId: string, workspaceId: string) => {
      const copy = await copyPageToWorkspace(store, pageId, workspaceId);
      if (copy) open({ kind: 'page', pageId: copy.id });
      refreshWorkspacesSoon();
    },
  };
}
